import type { LancarPOSDatabase } from '@lancar-pos/db-schema';

const MIGRATION_FLAG_KEY = '_migration_completed';
const MIGRATION_VERSION = 1;

interface LegacyProduct {
  id: string;
  name: string;
  sku: string;
  barcode: string;
  price: number;
  cost: number;
  categoryId: string | null;
  unit: string;
  imageUrl: string | null;
  stock: number;
  minStock: number;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

interface LegacyCategory {
  id: string;
  name: string;
  color: string;
  icon: string;
  sortOrder: number;
  parentId: string | null;
}

interface LegacyOrderItem {
  id: string;
  productId: string;
  productName: string;
  quantity: number;
  price: number;
  discount: number;
  subtotal: number;
  variant: { id: string; name: string; priceModifier: number } | null;
  notes: string;
}

interface LegacyOrder {
  id: string;
  orderNumber: string;
  items: LegacyOrderItem[];
  subtotal: number;
  discount: number;
  tax: number;
  total: number;
  paymentMethod: 'cash' | 'card' | 'qris' | 'mixed';
  paidAmount: number;
  changeAmount: number;
  status: 'draft' | 'pending' | 'completed' | 'cancelled' | 'refunded';
  customerId: string | null;
  createdAt: string;
}

interface LegacyCustomer {
  id: string;
  name: string;
  phone: string;
  email: string | null;
  address: string | null;
  notes: string | null;
  totalSpent: number;
  visitCount: number;
  createdAt: string;
  updatedAt: string;
}

function getLocalStorageItem<T>(key: string): T | null {
  try {
    const item = localStorage.getItem(key);
    if (!item) return null;
    const parsed = JSON.parse(item);
    return parsed.state || parsed;
  } catch {
    return null;
  }
}

function getSettingCategory(key: string): string {
  if (key.startsWith('sheets_')) return 'sheets';
  if (key.startsWith('tax_')) return 'tax';
  if (key.startsWith('business_')) return 'business';
  if (key.startsWith('printer_')) return 'printer';
  if (key.startsWith('onboarding_')) return 'onboarding';
  return 'general';
}

export async function migrateFromZustand(db: LancarPOSDatabase): Promise<void> {
  const existingFlag = await db.settings.findOne(MIGRATION_FLAG_KEY).exec();
  if (existingFlag) {
    const flagValue = existingFlag.value as { version: number } | undefined;
    if (flagValue && flagValue.version >= MIGRATION_VERSION) {
      return;
    }
  }

  const now = new Date().toISOString();

  // Migrate Products
  const productsData = getLocalStorageItem<{ products: LegacyProduct[] }>('products-storage');
  if (productsData?.products?.length) {
    const productsToInsert = productsData.products.map((p) => ({
      id: p.id,
      name: p.name,
      sku: p.sku || '',
      barcode: p.barcode || '',
      price: p.price,
      cost: p.cost || 0,
      categoryId: p.categoryId,
      unit: p.unit || 'pcs',
      imageUrl: p.imageUrl,
      stock: p.stock || 0,
      minStock: p.minStock || 0,
      isActive: p.isActive !== false,
      variants: [],
      bomItems: [],
      createdAt: p.createdAt || now,
      updatedAt: p.updatedAt || now,
      _deleted: false,
    }));

    if (productsToInsert.length > 0) {
      await db.products.bulkInsert(productsToInsert);
    }
  }

  // Migrate Categories
  const categoriesData = getLocalStorageItem<{ categories: LegacyCategory[] }>('categories-storage');
  if (categoriesData?.categories?.length) {
    const categoriesToInsert = categoriesData.categories.map((c) => ({
      id: c.id,
      name: c.name,
      color: c.color || '#6B7280',
      icon: c.icon || 'Tag',
      sortOrder: c.sortOrder || 0,
      parentId: c.parentId,
      createdAt: now,
      updatedAt: now,
      _deleted: false,
    }));

    if (categoriesToInsert.length > 0) {
      await db.categories.bulkInsert(categoriesToInsert);
    }
  }

  // Migrate Orders
  const ordersData = getLocalStorageItem<{ orders: LegacyOrder[] }>('orders-storage');
  if (ordersData?.orders?.length) {
    const ordersToInsert = ordersData.orders.map((o) => ({
      id: o.id,
      orderNumber: o.orderNumber,
      items: o.items || [],
      subtotal: o.subtotal || 0,
      discount: o.discount || 0,
      discountType: 'amount' as const,
      tax: o.tax || 0,
      total: o.total,
      paymentMethod: o.paymentMethod || 'cash',
      paidAmount: o.paidAmount || o.total,
      changeAmount: o.changeAmount || 0,
      status: o.status || 'completed',
      customerId: o.customerId,
      tableNumber: null,
      orderType: 'takeaway' as const,
      kitchenNotes: '',
      receiptPrinted: false,
      syncedAt: null,
      createdAt: o.createdAt || now,
      updatedAt: now,
      _deleted: false,
    }));

    if (ordersToInsert.length > 0) {
      await db.orders.bulkInsert(ordersToInsert);
    }
  }

  // Migrate Customers
  const customersData = getLocalStorageItem<{ customers: LegacyCustomer[] }>('customers-storage');
  if (customersData?.customers?.length) {
    const customersToInsert = customersData.customers.map((c) => ({
      id: c.id,
      name: c.name,
      phone: c.phone,
      email: c.email,
      address: c.address,
      notes: c.notes,
      totalSpent: c.totalSpent || 0,
      visitCount: c.visitCount || 0,
      createdAt: c.createdAt || now,
      updatedAt: c.updatedAt || now,
      _deleted: false,
    }));

    if (customersToInsert.length > 0) {
      await db.customers.bulkInsert(customersToInsert);
    }
  }

  // Migrate Settings
  const settingsData = getLocalStorageItem<{ settings: Record<string, unknown> }>('settings-storage');
  if (settingsData?.settings) {
    const settingsToInsert = Object.entries(settingsData.settings).map(([key, value]) => ({
      id: key,
      key,
      value,
      category: getSettingCategory(key),
      updatedAt: now,
    }));

    if (settingsToInsert.length > 0) {
      await db.settings.bulkInsert(settingsToInsert);
    }
  }

  // Set migration flag
  await db.settings.upsert({
    id: MIGRATION_FLAG_KEY,
    key: MIGRATION_FLAG_KEY,
    value: { version: MIGRATION_VERSION, date: now },
    category: '_system',
    updatedAt: now,
  });
}
